import { useEffect, useRef, useState } from 'react'
import { DEFAULT_PAGE } from '../../components/Table/constants'
import axios, { CancelTokenSource } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from '../../hooks/hooks'
import { setLoading } from '../../redux/store/common/slice'
import { ReactComponent as Close16 } from '../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { Input } from '../../components/Input'
import { Table } from '../../components/Table'
import { columns, cpColumns } from './constants'
import { isTableHasParams } from '../../helper/common'
import {
  TCCListFiltersFunc,
  TContractCategoriesList,
  TContractsCategoriesListFilters,
  TContractsCategoriesListParams,
  TContractsCategoriesTableRequestData
} from './types'
import { getContractsCategoriesList } from './api'
import './style.scss'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { ReactComponent as Plus16 } from '../../assets/svg/Plus16.svg'
import { BTN_TXT } from '../../constants'
import useRouter from '../../hooks/useRouter'
import { routes } from '../../router'
import { useContractPageContext } from '../../pages/Contracts/Providers/ContractPageContextProvider'
import { getUser } from '../../redux/store/user/getters'
import { ROLES } from '../Permission'
import { AssignCOLevelResponsiblePopup } from './AssignCOLevelResponsiblePopup/AssignCOLevelResponsiblePopup'
import { useContractCategoryDetailsPopup } from './Providers/CategoriesPopupProvider'
import cn from 'classnames'

interface IProps {
  shortView?: boolean
}

const CategoriesTable = ({ shortView }: IProps) => {
  const [tableData, setTableData] = useState<TContractCategoriesList[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] =
    useState<TContractsCategoriesTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [filters, setFilters] = useState<TContractsCategoriesListFilters>({})
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const dispatch = useDispatch()
  const debouncedSearch = useDebounce(searchValue, 500)

  const user = useSelector(getUser)

  const {
    addResponsibleUserPopup: {
      actions: { open }
    }
  } = useContractCategoryDetailsPopup()

  const { state } = useContractPageContext()

  const { navigate } = useRouter()

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])
  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: TContractsCategoriesTableRequestData) => {
    dispatch(setLoading(true))
    if (tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: TContractsCategoriesListParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()

    getContractsCategoriesList({
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const applyFilters: TCCListFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    const newPageInfo = { ...pageInfo, pageNumber: 1 }
    setFilters(newFilters)
    setPageInfo(newPageInfo)
    getTableData({ ...sortParams, filters: newFilters, page: newPageInfo })
  }

  return (
    <div className="table-wrapper community-users">
      <div className="table-wrapper__header">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: 'Search by Contract Category',
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
        <div className="table-wrapper__header-buttons">
          {state.isCanSetUpEditCategory && (
            <Button
              type={BUTTON_TYPES.PRIMARY}
              icon={<Plus16 />}
              onClick={() =>
                navigate(`${routes.contracts}${routes.contractCategories}/new`)
              }
              upperCase
            >
              {BTN_TXT.SET_UP_CATEGORY}
            </Button>
          )}
        </div>
      </div>
      {!firstLoad && (
        <Table<TContractCategoriesList>
          dataSource={tableData}
          columns={
            !shortView
              ? columns({
                  applyFilters,
                  filters,
                  userUuid: user.uuid,
                  showOrganization: !!user.commons
                })
              : cpColumns({
                  showResponsibleUser: user.role === ROLES.COMMUNITY_PRESIDENT,
                  openAssignPopup: open
                })
          }
          className={cn('contracts-table contracts-page categories-table', {
            'full-width': user.role === ROLES.COMMUNITY_PRESIDENT
          })}
          onChange={handleTableChange}
          hasSearchOrFilters={isTableHasParams<TContractsCategoriesListFilters>(
            debouncedSearch,
            filters
          )}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
        />
      )}
      <AssignCOLevelResponsiblePopup
        onAssign={() =>
          getTableData({
            ...sortParams,
            filters,
            page: pageInfo
          })
        }
      />
    </div>
  )
}
export default CategoriesTable
